import { Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild, AfterViewInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { TimelineActivitiesComponent } from 'app/layout/common/timeline-activities/timeline-activities.component';
import { UpdatesRequiredComponent } from 'app/layout/common/updates-required/updates-required.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
// TEMP: `AddUserButtonComponent` import removed while the
// `<add-user-button>` element is hidden from the topbar. To restore
// it, uncomment the import below AND the matching entry in the
// `imports: [...]` array below AND the markup in the template
// (search the template for `TEMP: Add user button`).
// import { AddUserButtonComponent } from 'app/layout/common/add-user-button/add-user-button.component';
import { QuickAddUserComponent } from 'app/layout/common/quick-add-user/quick-add-user.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { VoiceCallsComponent } from 'app/layout/common/voice-calls/voice-calls.component';
import { VideoCallsComponent } from 'app/layout/common/video-calls/video-calls.component';
import { ActiveCallComponent } from 'app/modules/admin/voice/active-call/active-call.component';
import { McpDrawerComponent } from 'app/layout/common/mcp-drawer/mcp-drawer.component';
import { McpRealtimeRouterService } from 'app/core/services/realtime-segments/mcp-realtime-router.service';
import { CommonModule } from '@angular/common';
import { isPlatformAdmin, isServiceUnit } from 'app/core/user/domain-roles';
import {
    AssistCompany,
    AssistContextService,
} from 'app/core/assist-context/assist-context.service';

@Component({
    selector: 'dense-layout',
    standalone: true,
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        /* LanguagesComponent, */
        FuseFullscreenComponent,
        /* SearchComponent,
        ShortcutsComponent, */
      /*   NotificationsComponent, */
        UserComponent,
        RouterOutlet,
        RouterModule,
        QuickChatComponent,
        MatSidenavModule,
        MatTooltipModule,   
        MessagesComponent,
        // TEMP: AddUserButtonComponent removed from imports while
        // the template element is hidden (see top-of-file comment).
        // AddUserButtonComponent,
        QuickAddUserComponent,
        TimelineActivitiesComponent,
        UpdatesRequiredComponent,
        VoiceCallsComponent,
        // TEMP: VideoCallsComponent removed from imports while the template
        // element is hidden (see <video-calls> comment in the HTML).
        // VideoCallsComponent,
        ActiveCallComponent,
        McpDrawerComponent,
        CommonModule,
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    navigationAppearance: 'default' | 'dense' = 'default';
    currentTopbarTime = '';
    agentEnabled = false;
    canUseCompanyPicker = false;
    companyPickerOpen = false;
    assistCompanies: AssistCompany[] = [];
    assistCompany: AssistCompany | null = null;
    private _clockInterval: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('profileDrawer') profileDrawer: any;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _userService: UserService,
        public mcpRealtime: McpRealtimeRouterService,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _assistContext: AssistContextService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get selfCompanyLabel(): string {
        return this.user?.company_name || 'District Printing';
    }

    get selfUserName(): string {
        return this.user?.name || this.user?.email || 'Signed-in user';
    }

    get selfUserRole(): string {
        return this.user?.role || 'Staff';
    }

    get selfCompanyLogo(): string {
        return this.user?.company_logo || 'images/logo/dp_logo.png';
    }

    get selfProfileImage(): string | null {
        return this.user?.avatar || (this.user as any)?.profile_image || null;
    }

    get selectedCompanyLabel(): string {
        return this.assistCompany
            ? this.companyMenuLabel(this.assistCompany)
            : this.selfCompanyLabel;
    }

    get selectedUserName(): string {
        return this.assistCompany
            ? this.companyUserName(this.assistCompany)
            : this.selfUserName;
    }

    get selectedUserRole(): string {
        return this.assistCompany
            ? this.companyUserRole(this.assistCompany)
            : this.selfUserRole;
    }

    get selectedCompanyLogo(): string | null {
        return this.assistCompany?.company_logo || this.selfCompanyLogo;
    }

    get selectedProfileImage(): string | null {
        return this.assistCompany
            ? this.companyProfileImage(this.assistCompany)
            : this.selfProfileImage;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.agentEnabled = false;
        window.addEventListener('agent1-toggle', this._onAgentToggle as EventListener);

        this._updateTopbarTime();
        this._clockInterval = setInterval(() => this._updateTopbarTime(), 1000);

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((user: User) => {
                        this.user = user;
                        this.canUseCompanyPicker = isServiceUnit(user);
                        this.agentEnabled = this._isAgentUiEnabled();
                        this._assistContext.initForUser(user);
                    });

        this._assistContext.companies$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((companies) => {
                this.assistCompanies = companies || [];
            });

        this._assistContext.company$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((company) => {
                this.assistCompany = company;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Keep dense-layout navigation permanently expanded on desktop.
                this.navigationAppearance = 'default';
            });

        try {
            window.addEventListener('open-main-navigation', () => {
                try { this.toggleNavigation('mainNavigation'); } catch {}
            });
        } catch {}

        // Prewarm MCP realtime session so first interaction is faster.
        setTimeout(() => {
            if (!this._isAgentUiEnabled()) {
                return;
            }
            this.mcpRealtime.prewarm().catch((err) => {
                console.log('[MCP] Prewarm skipped:', err?.message ?? err);
            });
        }, 1200);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (this._clockInterval) clearInterval(this._clockInterval);
        window.removeEventListener('agent1-toggle', this._onAgentToggle as EventListener);
    }

    ngAfterViewInit(): void {
        // The topbar "+ user" entry point is now owned by
        // `<add-user-button>` (which handles `open-add-user-drawer`
        // and `prefill-quick-add-user` window events directly and
        // routes through `AddUserDrawerHostService`).  The layout
        // only retains the profile-drawer hook here.
        try {
            window.addEventListener('open-profile-drawer', () => {
                try { console.log('[Profile][Layout:Dense] open'); this.profileDrawer?.open?.(); } catch (e) { console.error('[Profile][Layout:Dense] fail', e); }
            });
        } catch {}
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance = 'default';
    }

    toggleCompanyPicker(): void {
        this.companyPickerOpen = !this.companyPickerOpen;
    }

    selectCompany(company: AssistCompany | null): void {
        this._assistContext.selectCompany(company);
        this.companyPickerOpen = false;
    }

    companyMenuLabel(company: AssistCompany): string {
        return this._assistContext.displayLabel(company);
    }

    companyUserName(company: AssistCompany): string {
        const name = `${company?.first_name || ''} ${company?.last_name || ''}`.trim();
        return name || company?.email || 'Account owner';
    }

    companyUserRole(company: AssistCompany): string {
        return company?.role || company?.kind || 'Business account';
    }

    companyProfileImage(company: AssistCompany): string | null {
        return company?.profile_image || company?.avatar || null;
    }

    initials(value: string): string {
        return (value || '?')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join('') || '?';
    }

    isAssistCompanyActive(company: AssistCompany): boolean {
        return !!this.assistCompany
            && String(this.assistCompany.id) === String(company?.id);
    }

    async toggleMcpMic(): Promise<void> {
        if (!this._isAgentUiEnabled()) {
            return;
        }
        this.mcpRealtime.switchSegment('openai');
        const status = this.mcpRealtime.status$.value;
        if (status === 'idle' || status === 'error') {
            await this.mcpRealtime.start();
            this.mcpRealtime.setMicMuted(false);
        } else if (status === 'active') {
            const muted = this.mcpRealtime.micMuted$.value;
            this.mcpRealtime.setMicMuted(!muted);
        }
    }

    async toggleGeminiMic(): Promise<void> {
        if (!this._isAgentUiEnabled()) {
            return;
        }
        this.mcpRealtime.switchSegment('gemini');
        const micOk = await this.primeBrowserMicPermission();
        if (!micOk) {
            return;
        }
        await this.mcpRealtime.start();
        this.mcpRealtime.setMicMuted(false);
    }

    private _isAgentUiEnabled(): boolean {
        return isPlatformAdmin(this.user)
            && localStorage.getItem('agent1.enabled') === 'true';
    }

    private _onAgentToggle = (e: CustomEvent) => {
        this.agentEnabled = !!e?.detail?.enabled && isPlatformAdmin(this.user);
    };

    private async primeBrowserMicPermission(): Promise<boolean> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
            return true;
        } catch (err: any) {
            console.error('[Gemini] Microphone permission denied/unavailable:', err);
            return false;
        }
    }

    private _updateTopbarTime(): void {
        const now = new Date();
        const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
        this.currentTopbarTime = `${date} ${time}`;
    }
}
